import { Lock, RotateCcw } from 'lucide-react';
import { NON_OVERRIDABLE_ACTIONS, POLICIES, can, type Action } from '@/services/auth/capabilities';
import { useUserProfile } from '@/services/user/hooks';
import { useDevOverrides, useDevOverridesStore } from '@/stores/devOverridesStore';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const ACTIONS = (Object.keys(POLICIES) as Action[])
  .filter((a) => !NON_OVERRIDABLE_ACTIONS.includes(a))
  .sort();

type Tri = boolean | undefined;

function Segmented({ value, onChange }: { value: Tri; onChange: (v: Tri) => void }) {
  const opts: { label: string; v: Tri }[] = [
    { label: 'Auto', v: undefined },
    { label: 'On', v: true },
    { label: 'Off', v: false },
  ];
  return (
    <div className="inline-flex shrink-0 overflow-hidden rounded-md border border-border/60">
      {opts.map((o, i) => (
        <button
          key={o.label}
          type="button"
          onClick={() => onChange(o.v)}
          className={cn(
            'px-2 py-0.5 text-[11px] transition-colors',
            i > 0 && 'border-l border-border/60',
            value === o.v
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted'
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function CapabilityOverrides() {
  const profile = useUserProfile();
  const overrides = useDevOverrides();
  const setOverride = useDevOverridesStore((s) => s.setOverride);
  const reset = useDevOverridesStore((s) => s.reset);

  const activeCount = Object.keys(overrides).length;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        Force a capability on or off to preview the UI as another role would see it. Client-side
        only — the backend still enforces your real roles, so writes will fail even when a
        capability is forced on. Persists across reloads.
      </p>

      {activeCount > 0 && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2">
          <span className="text-xs font-medium text-foreground">
            {activeCount} override{activeCount > 1 ? 's' : ''} active — UI does not reflect your real
            access.
          </span>
          <Button size="xs" variant="outline" onClick={() => reset()}>
            <RotateCcw size={12} /> Reset
          </Button>
        </div>
      )}

      <ul className="flex flex-col gap-1.5">
        {ACTIONS.map((action) => {
          const base = can(action, profile);
          const override = overrides[action];
          const effective = override ?? base;
          return (
            <li
              key={action}
              className="flex items-center gap-2 rounded-lg border border-border/50 px-3 py-2"
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 shrink-0 rounded-full',
                  effective ? 'bg-success' : 'bg-muted-foreground/40'
                )}
                title={effective ? 'allowed' : 'denied'}
              />
              <span className="min-w-0 flex-1 truncate font-mono text-xs" title={action}>
                {action}
              </span>
              {override !== undefined && (
                <Badge variant="warning" className="text-[10px]">
                  forced
                </Badge>
              )}
              <Segmented value={override} onChange={(v) => setOverride(action, v)} />
            </li>
          );
        })}
      </ul>

      <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Lock size={11} /> admin.dev-panel is not overridable, so you cannot lock yourself out.
      </p>
    </div>
  );
}
